import { useContext, useState } from 'react';
import api, { type TokenResponse } from '@/lib/api';
import HttpMethod from '@/lib/constants/HttpMethod.enum';
import AuthenticationStateContext from '@/components/App/AppSetup/AuthenticationState/AuthenticationStateContext';
import { useMsal } from '@azure/msal-react';
import { BrowserAuthError } from '@azure/msal-browser';

const useLoginWithMicrosoft = (
  onShowError: (error: unknown) => void,
): [() => Promise<void>, boolean] => {
  const [, refreshAuthenticationState] = useContext(AuthenticationStateContext);
  const { instance: msalInstance } = useMsal();

  const [loginPopupOpen, setLoginPopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const loginWithMicrosoft = async (): Promise<void> => {
    if (loginPopupOpen) {
      return;
    }

    try {
      setLoginPopupOpen(true);

      const loginResponse = await msalInstance.loginPopup({
        scopes: ['User.Read', 'openid', 'profile', 'email'],
        prompt: 'select_account',
      });

      msalInstance.setActiveAccount(loginResponse.account);

      setLoading(true);

      const response =
        await api.callWithoutAuthorizationHandling<TokenResponse>(
          'auth/login/microsoft',
          {
            method: HttpMethod.POST,
            body: JSON.stringify({
              idToken: loginResponse.idToken,
              accessToken: loginResponse.accessToken,
            }),
          },
        );

      api.authenticate(response.accessToken, response.refreshToken);

      refreshAuthenticationState();
    } catch (error) {
      if (
        error instanceof BrowserAuthError &&
        error.errorCode === 'user_cancelled'
      ) {
        return;
      }

      onShowError(error);
    } finally {
      setLoginPopupOpen(false);
      setLoading(false);
    }
  };

  return [loginWithMicrosoft, loading];
};

export default useLoginWithMicrosoft;
